// src/features/customers/components/ContactsManager.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, Star, Eye, EyeOff, Package, Mail, FileText, QrCode, ExternalLink } from 'lucide-react';
import { CustomerContact } from '../../../shared/types';
import { customerContactService } from '../../../services/customer/CustomerContactService';
import { supabase } from '../../../shared/services/supabase';
import { netlifyFetch } from '../../../shared/services/netlifyFetch';

interface ContactsManagerProps {
  customerId: string;
  onUpdate: () => void;
}

interface ContactFormData {
  vorname: string;
  nachname: string;
  email: string;
  email_cc: string;
  anrede: 'Du' | 'Sie' | '';
  geschlecht: 'männlich' | 'weiblich' | 'divers' | '';
  rolle: string;
  is_primary: boolean;
  receives_finalization_package: boolean;
  receives_lohnschein_report: boolean;
  receives_oppm_payslips_qr: boolean;
  receives_monatsuebersicht_qr: boolean;
  passwort: string;
}

const emptyForm: ContactFormData = {
  vorname: '',
  nachname: '',
  email: '',
  email_cc: '',
  anrede: 'Sie',
  geschlecht: '',
  rolle: '',
  is_primary: false,
  receives_finalization_package: false,
  receives_lohnschein_report: false,
  receives_oppm_payslips_qr: false,
  receives_monatsuebersicht_qr: false,
  passwort: ''
};

export const ContactsManager: React.FC<ContactsManagerProps> = ({
  customerId,
  onUpdate
}) => {
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newForm, setNewForm] = useState<ContactFormData>({ ...emptyForm });
  const [editForm, setEditForm] = useState<ContactFormData>({ ...emptyForm });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [invitingContacts, setInvitingContacts] = useState<Set<string>>(new Set());

  // Ob der Kunde zur OPPM-Gruppe gehört. Steuert die Sichtbarkeit der beiden
  // OPPM-Checkboxen. Wird einmalig beim Laden ermittelt (die Gruppe wird in
  // dieser Komponente nicht bearbeitet). Nicht-OPPM-Kunden sehen die OPPM-
  // Optionen nicht, um Verwirrung zu vermeiden.
  const [isOppmCustomer, setIsOppmCustomer] = useState(false);

  // Simulation im Kundenportal (25.09.2026): Der Knopf erscheint nur für
  // Mitarbeiter mit dem Merkmal darf_portal_simulieren. Die Anzeige ist eine
  // Bequemlichkeit; die Berechtigung prüft die Netlify-Funktion
  // portal-simulation-start anhand der persönlichen Anmeldung.
  const [darfSimulieren, setDarfSimulieren] = useState(false);
  const [simuliertGerade, setSimuliertGerade] = useState<string | null>(null);

  useEffect(() => {
    const identitaet = localStorage.getItem('authToken');
    if (!identitaet) return;
    let email = '';
    try {
      const teil = identitaet.split('.')[1] || '';
      const nutzlast = JSON.parse(atob(teil.replace(/-/g, '+').replace(/_/g, '/')));
      email = String(nutzlast.email || '').trim().toLowerCase();
    } catch {
      return;
    }
    if (!email) return;
    supabase
      .from('xtax_staff')
      .select('darf_portal_simulieren')
      .ilike('email', email)
      .maybeSingle()
      .then(({ data }) => setDarfSimulieren(data?.darf_portal_simulieren === true));
  }, []);

  const handleSimulation = async (contact: CustomerContact) => {
    const identitaet = localStorage.getItem('authToken');
    if (!identitaet) {
      alert('Für die Simulation ist die persönliche Anmeldung im Intranet erforderlich. Bitte neu anmelden.');
      return;
    }
    // Den Tab sofort im Klick öffnen, sonst blockiert der Browser ihn als Pop-up
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setSimuliertGerade(contact.id);
    try {
      const res = await netlifyFetch('/.netlify/functions/portal-simulation-start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${identitaet}` },
        body: JSON.stringify({ contact_id: contact.id })
      });
      const daten = await res.json().catch(() => ({}));
      if (!res.ok || !daten.success || !daten.url) {
        throw new Error(daten.error || `HTTP ${res.status}`);
      }
      if (tab) {
        tab.location.href = daten.url;
      } else {
        window.open(daten.url, '_blank', 'noopener');
      }
    } catch (error) {
      if (tab) tab.close();
      alert(`Die Simulation konnte nicht gestartet werden: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setSimuliertGerade(null);
    }
  };

  const loadContacts = useCallback(async () => {
    setLoading(true);
    const data = await customerContactService.getAllContactsForCustomer(customerId);
    setContacts(data);

    // OPPM-Gruppenzugehörigkeit des Kunden ermitteln. Zwei getrennte Abfragen
    // (kein PostgREST-Embed auf customers), Zuordnung über den Gruppennamen "OPPM".
    let oppm = false;
    const { data: customerRow } = await supabase
      .from('customers')
      .select('customer_group_id')
      .eq('id', customerId)
      .single();
    if (customerRow?.customer_group_id) {
      const { data: groupRow } = await supabase
        .from('customer_groups')
        .select('name')
        .eq('id', customerRow.customer_group_id)
        .single();
      oppm = groupRow?.name === 'OPPM';
    }
    setIsOppmCustomer(oppm);

    setLoading(false);
  }, [customerId]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // ============================================
  // SYNC: Primären Ansprechpartner → customers-Tabelle
  // Hält die bestehenden Felder in der customers-Tabelle aktuell,
  // damit alle Services, die customers.email etc. lesen, weiterhin funktionieren.
  // HINWEIS: secure_download_passwort wird NICHT synchronisiert (Hash vs. Klartext Unterschied)
  // ============================================

  const syncPrimaryToCustomer = async () => {
    const primary = await customerContactService.getPrimaryContact(customerId);
    if (primary) {
      await supabase
        .from('customers')
        .update({
          email: primary.email,
          email_cc: primary.email_cc,
          vorname: primary.vorname,
          nachname: primary.nachname,
          anrede: primary.anrede,
          geschlecht: primary.geschlecht,
          updated_at: new Date().toISOString()
        })
        .eq('id', customerId);
    }
  };

  // ============================================
  // ADD
  // ============================================

  const handleAdd = async () => {
    if (!newForm.email.trim()) return;

    const result = await customerContactService.createContact({
      customer_id: customerId,
      vorname: newForm.vorname.trim() || null,
      nachname: newForm.nachname.trim() || null,
      email: newForm.email.trim(),
      email_cc: newForm.email_cc.trim() || null,
      anrede: newForm.anrede === '' ? null : newForm.anrede,
      geschlecht: newForm.geschlecht === '' ? null : newForm.geschlecht,
      rolle: newForm.rolle.trim() || null,
      is_primary: newForm.is_primary,
      receives_finalization_package: newForm.receives_finalization_package,
      receives_lohnschein_report: newForm.receives_lohnschein_report,
      receives_oppm_payslips_qr: isOppmCustomer ? newForm.receives_oppm_payslips_qr : false,
      receives_monatsuebersicht_qr: isOppmCustomer ? newForm.receives_monatsuebersicht_qr : false,
      secure_download_passwort: newForm.passwort.trim() || null
    });

    if (result) {
      await loadContacts();
      await syncPrimaryToCustomer();
      setNewForm({ ...emptyForm });
      setIsAdding(false);
      setShowNewPassword(false);
      onUpdate();
    }
  };

  // ============================================
  // EDIT
  // ============================================

  const handleStartEdit = (contact: CustomerContact) => {
    setEditingId(contact.id);
    setEditForm({
      vorname: contact.vorname || '',
      nachname: contact.nachname || '',
      email: contact.email,
      email_cc: contact.email_cc || '',
      anrede: contact.anrede || '',
      geschlecht: contact.geschlecht || '',
      rolle: contact.rolle || '',
      is_primary: contact.is_primary,
      receives_finalization_package: contact.receives_finalization_package,
      receives_lohnschein_report: contact.receives_lohnschein_report || false,
      receives_oppm_payslips_qr: contact.receives_oppm_payslips_qr || false,
      receives_monatsuebersicht_qr: contact.receives_monatsuebersicht_qr || false,
      passwort: '' // Passwort nie vorbelegen
    });
    setShowEditPassword(false);
  };

  const handleSaveEdit = async (contactId: string) => {
    if (!editForm.email.trim()) return;

    const updateData: any = {
      vorname: editForm.vorname.trim() || null,
      nachname: editForm.nachname.trim() || null,
      email: editForm.email.trim(),
      email_cc: editForm.email_cc.trim() || null,
      anrede: editForm.anrede === '' ? null : editForm.anrede,
      geschlecht: editForm.geschlecht === '' ? null : editForm.geschlecht,
      rolle: editForm.rolle.trim() || null,
      is_primary: editForm.is_primary,
      receives_finalization_package: editForm.receives_finalization_package,
      receives_lohnschein_report: editForm.receives_lohnschein_report,
      receives_oppm_payslips_qr: isOppmCustomer ? editForm.receives_oppm_payslips_qr : false,
      receives_monatsuebersicht_qr: isOppmCustomer ? editForm.receives_monatsuebersicht_qr : false
    };

    // Passwort nur aktualisieren wenn etwas eingegeben wurde
    if (editForm.passwort.trim()) {
      updateData.secure_download_passwort = editForm.passwort.trim();
    }

    const result = await customerContactService.updateContact(contactId, updateData);

    if (result) {
      await loadContacts();
      await syncPrimaryToCustomer();
      setEditingId(null);
      setShowEditPassword(false);
      onUpdate();
    }
  };

  // ============================================
  // DELETE
  // ============================================

  const handleDelete = async (contactId: string) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact?.is_primary && contacts.filter(c => c.is_active).length > 1) {
      alert('Der primäre Ansprechpartner kann nicht gelöscht werden, solange weitere Ansprechpartner existieren. Bitte setzen Sie zuerst einen anderen Ansprechpartner als primär.');
      return;
    }

    if (!window.confirm('Möchten Sie diesen Ansprechpartner wirklich löschen?')) return;

    const success = await customerContactService.deleteContact(contactId);
    if (success) {
      await loadContacts();
      await syncPrimaryToCustomer();
      onUpdate();
    }
  };

  // ============================================
  // PASSWORD SETUP INVITE
  // ============================================

  const handleSendPasswordInvite = async (contact: CustomerContact) => {
    if (invitingContacts.has(contact.id)) return;

    if (!contact.email) {
      alert('Dieser Ansprechpartner hat keine E-Mail-Adresse hinterlegt.');
      return;
    }

    const displayName = customerContactService.getContactDisplayName(contact);
    if (!window.confirm(`Passwort-Einrichtungslink an ${displayName} (${contact.email}) senden?`)) return;

    setInvitingContacts(prev => new Set(prev).add(contact.id));

    const result = await customerContactService.sendPasswordSetupInvite(contact.id);

    if (result.success) {
      alert(`Einladung erfolgreich an ${contact.email} gesendet.`);
    } else {
      alert(`Fehler: ${result.error}`);
    }

    setInvitingContacts(prev => {
      const next = new Set(prev);
      next.delete(contact.id);
      return next;
    });
  };

  // ============================================
  // RENDER: CONTACT FORM (shared between add & edit)
  // ============================================

  const renderContactForm = (
    form: ContactFormData,
    setForm: React.Dispatch<React.SetStateAction<ContactFormData>>,
    showPassword: boolean,
    setShowPassword: React.Dispatch<React.SetStateAction<boolean>>,
    isEdit: boolean
  ) => (
    <div style={{ display: 'grid', gap: '12px' }}>
      {/* Zeile 1: Vorname, Nachname */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            Vorname
          </label>
          <input
            type="text"
            value={form.vorname}
            onChange={(e) => setForm({ ...form, vorname: e.target.value })}
            placeholder="Vorname"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            Nachname
          </label>
          <input
            type="text"
            value={form.nachname}
            onChange={(e) => setForm({ ...form, nachname: e.target.value })}
            placeholder="Nachname"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {/* Zeile 2: E-Mail, E-Mail CC */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            E-Mail <span style={{ color: '#EF4444' }}>*</span>
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="E-Mail-Adresse"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            E-Mail CC
          </label>
          <input
            type="email"
            value={form.email_cc}
            onChange={(e) => setForm({ ...form, email_cc: e.target.value })}
            placeholder="CC-Empfänger"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {/* Zeile 3: Anrede, Geschlecht, Rolle */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            Anrede
          </label>
          <select
            value={form.anrede}
            onChange={(e) => setForm({ ...form, anrede: e.target.value as any })}
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
          >
            <option value="">– Wählen –</option>
            <option value="Sie">Sie</option>
            <option value="Du">Du</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            Geschlecht
          </label>
          <select
            value={form.geschlecht}
            onChange={(e) => setForm({ ...form, geschlecht: e.target.value as any })}
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
          >
            <option value="">– Wählen –</option>
            <option value="männlich">Männlich</option>
            <option value="weiblich">Weiblich</option>
            <option value="divers">Divers</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            Rolle
          </label>
          <input
            type="text"
            value={form.rolle}
            onChange={(e) => setForm({ ...form, rolle: e.target.value })}
            placeholder="z.B. Geschäftsführer"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {/* Zeile 4: Passwort */}
      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
          Passwort für geschützten Dokumentenbereich{isEdit ? ' (leer lassen = unverändert)' : ''}
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type={showPassword ? 'text' : 'password'}
            value={form.passwort}
            onChange={(e) => setForm({ ...form, passwort: e.target.value })}
            placeholder={isEdit ? 'Neues Passwort eingeben...' : 'Passwort festlegen...'}
            style={{
              width: '100%',
              padding: '8px 36px 8px 10px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              color: '#6B7280'
            }}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      {/* Zeile 5: Checkboxen */}
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          color: '#374151',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={form.is_primary}
            onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
            style={{ width: '16px', height: '16px' }}
          />
          <Star size={14} color={form.is_primary ? '#F59E0B' : '#9CA3AF'} fill={form.is_primary ? '#F59E0B' : 'none'} />
          Primärer Ansprechpartner
        </label>
      </div>
      {/* Die Empfangsschalter (Monatsabschluss, Lohnschein-Bericht, die beiden
          QR-Versände) und der Vollzugriff werden im Reiter „Versand und
          Sichtbarkeit" gepflegt. Bewusst nur dort, damit es für dieselbe
          Berechtigung nicht zwei Bedienstellen gibt. Die bestehenden Werte
          eines Kontakts bleiben beim Bearbeiten unverändert erhalten. */}
      <div style={{
        marginTop: '12px',
        fontSize: '12px',
        color: '#6B7280',
        lineHeight: '1.5'
      }}>
        Wer welche Pakete erhält und wer interne Mitarbeiter sehen darf, wird im Reiter
        „Versand und Sichtbarkeit" festgelegt.
      </div>
    </div>
  );

  // ============================================
  // RENDER: LOADING
  // ============================================

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Lade Ansprechpartner...</div>;
  }

  // ============================================
  // RENDER: MAIN
  // ============================================

  return (
    <div>
      {/* Header mit Add Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
            Ansprechpartner
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
            {contacts.length === 0 ? 'Noch keine Ansprechpartner angelegt' : `${contacts.length} Ansprechpartner`}
          </p>
        </div>
        {!isAdding && (
          <button
            onClick={() => {
              setIsAdding(true);
              setNewForm({ ...emptyForm, is_primary: contacts.length === 0 });
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: '#10B981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <Plus size={16} />
            Hinzufügen
          </button>
        )}
      </div>

      {/* Add Form */}
      {isAdding && (
        <div style={{
          border: '1px solid #BBF7D0',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          backgroundColor: '#F0FDF4'
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#166534' }}>
            Neuer Ansprechpartner
          </h4>
          {renderContactForm(newForm, setNewForm, showNewPassword, setShowNewPassword, false)}
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setIsAdding(false);
                setNewForm({ ...emptyForm });
                setShowNewPassword(false);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#F3F4F6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleAdd}
              disabled={!newForm.email.trim()}
              style={{
                padding: '8px 16px',
                backgroundColor: newForm.email.trim() ? '#10B981' : '#9CA3AF',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: newForm.email.trim() ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Speichern
            </button>
          </div>
        </div>
      )}

      {/* Contact List */}
      {contacts.length > 0 && (
        <div style={{ display: 'grid', gap: '12px' }}>
          {contacts.map(contact => (
            <div
              key={contact.id}
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '16px',
                backgroundColor: contact.is_active ? 'white' : '#F9FAFB',
                opacity: contact.is_active ? 1 : 0.6
              }}
            >
              {editingId === contact.id ? (
                /* ===== EDIT MODE ===== */
                <div>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Ansprechpartner bearbeiten
                  </h4>
                  {renderContactForm(editForm, setEditForm, showEditPassword, setShowEditPassword, true)}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setShowEditPassword(false);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#F3F4F6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={() => handleSaveEdit(contact.id)}
                      disabled={!editForm.email.trim()}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: editForm.email.trim() ? '#10B981' : '#9CA3AF',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: editForm.email.trim() ? 'pointer' : 'not-allowed',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      Speichern
                    </button>
                  </div>
                </div>
              ) : (
                /* ===== VIEW MODE ===== */
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    {/* Name + Badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                        {customerContactService.getContactDisplayName(contact)}
                      </span>
                      {contact.is_primary && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          backgroundColor: '#FEF3C7',
                          color: '#92400E',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          <Star size={10} fill="#F59E0B" color="#F59E0B" />
                          Primär
                        </span>
                      )}
                      {contact.receives_finalization_package && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          backgroundColor: '#DBEAFE',
                          color: '#1E40AF',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          <Package size={10} />
                          Monatsabschluss
                        </span>
                      )}
                      {contact.receives_lohnschein_report && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          backgroundColor: '#D1FAE5',
                          color: '#065F46',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          <FileText size={10} />
                          Lohnschein-Bericht
                        </span>
                      )}
                      {contact.receives_oppm_payslips_qr && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          backgroundColor: '#EDE9FE',
                          color: '#5B21B6',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          <QrCode size={10} />
                          OPPM QR-Code
                        </span>
                      )}
                      {contact.receives_monatsuebersicht_qr && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          backgroundColor: '#CCFBF1',
                          color: '#0F766E',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          <QrCode size={10} />
                          Monatsübersicht QR
                        </span>
                      )}
                      {contact.secure_download_passwort && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          backgroundColor: '#F0FDF4',
                          color: '#166534',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          🔒 Passwort
                        </span>
                      )}
                    </div>

                    {/* Details */}
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#6B7280' }}>
                      <span>{contact.email}</span>
                      {contact.email_cc && <span> · CC: {contact.email_cc}</span>}
                      {contact.rolle && <span> · {contact.rolle}</span>}
                      {contact.anrede && <span> · {contact.anrede}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '6px', marginLeft: '12px', flexShrink: 0 }}>
                    {darfSimulieren && contact.is_active && contact.email && (
                      <button
                        onClick={() => handleSimulation(contact)}
                        disabled={simuliertGerade === contact.id}
                        style={{
                          padding: '6px',
                          backgroundColor: simuliertGerade === contact.id ? '#E5E7EB' : '#FEF2F2',
                          color: simuliertGerade === contact.id ? '#9CA3AF' : '#B91C1C',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: simuliertGerade === contact.id ? 'not-allowed' : 'pointer'
                        }}
                        title="Im Kundenportal als dieser Ansprechpartner ansehen (Simulation)"
                      >
                        <ExternalLink size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => handleSendPasswordInvite(contact)}
                      disabled={invitingContacts.has(contact.id)}
                      style={{
                        padding: '6px',
                        backgroundColor: invitingContacts.has(contact.id) ? '#E5E7EB' : '#EFF6FF',
                        color: invitingContacts.has(contact.id) ? '#9CA3AF' : '#1E40AF',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: invitingContacts.has(contact.id) ? 'not-allowed' : 'pointer'
                      }}
                      title="Passwort-Einrichtungslink senden"
                    >
                      <Mail size={16} />
                    </button>
                    <button
                      onClick={() => handleStartEdit(contact)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#F3F4F6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Bearbeiten"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(contact.id)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#FEE2E2',
                        color: '#991B1B',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Löschen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {contacts.length === 0 && !isAdding && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#9CA3AF',
          fontSize: '14px'
        }}>
          <p>Noch keine Ansprechpartner angelegt.</p>
          <p style={{ fontSize: '13px', marginTop: '4px' }}>
            Der bei der Migration übernommene Ansprechpartner wurde bereits automatisch erstellt.
          </p>
        </div>
      )}
    </div>
  );
};
